/**
 * Dialog de configuração de credenciais de um provider de assinatura digital
 * 
 * Formulário dinâmico por provider com campos sensíveis mascarados.
 * Suporta: Autentique, DocuSign e ZapSign.
 * 
 * RELEASE: v1.0.0 - 2026-02-16
 * v1.1.0: Adicionado campo webhook_secret para validação HMAC
 */

import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Loader2, Eye, EyeOff, Wifi, Trash2, Copy, CheckCircle, Link } from 'lucide-react';
import { toast } from 'sonner';
import {
  useTenantSignatureConfigs,
  type SignatureProvider,
  type TenantSignatureConfig,
} from '@/hooks/useTenantSignatureConfigs';
import { supabase } from '@/integrations/supabase/client';
import { getEdgeFunctionUrl } from '@/lib/supabaseHelpers';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

// Logger para rastreamento
const logger = createLogger('SignatureConfigDialog');

// =============================================================================
// TIPOS
// =============================================================================

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  provider: SignatureProvider | null;
  existingConfig: TenantSignatureConfig | null;
}

/** Definição de campo do formulário por provider */
interface FieldDef {
  key: string;
  label: string;
  placeholder: string;
  sensitive: boolean;
  required: boolean;
  helpText?: string;
}

// =============================================================================
// CAMPOS POR PROVIDER
// =============================================================================

/** Campos de configuração específicos de cada provider */
const PROVIDER_FIELDS: Record<string, FieldDef[]> = {
  autentique: [
    {
      key: 'api_token',
      label: 'API Token',
      placeholder: 'Cole o token da API Autentique',
      sensitive: true,
      required: true,
      helpText: 'Encontre em: Autentique > Configurações > API',
    },
    {
      key: 'webhook_secret',
      label: 'Webhook Secret (Endpoint Secret)',
      placeholder: 'Cole o Endpoint Secret gerado pela Autentique',
      sensitive: true,
      required: false,
      helpText: 'Gerado ao configurar o webhook no Painel do Desenvolvedor da Autentique. Usado para validar autenticidade dos callbacks.',
    },
  ],
  docusign: [
    {
      key: 'integration_key',
      label: 'Integration Key',
      placeholder: 'Integration Key do DocuSign',
      sensitive: true,
      required: true,
    },
    {
      key: 'secret_key',
      label: 'Secret Key',
      placeholder: 'Secret Key do DocuSign',
      sensitive: true,
      required: true,
    },
    {
      key: 'account_id',
      label: 'Account ID',
      placeholder: 'Account ID do DocuSign',
      sensitive: false,
      required: true,
    },
  ],
  zapsign: [
    {
      key: 'api_token',
      label: 'API Token',
      placeholder: 'Cole o token da API ZapSign',
      sensitive: true,
      required: true,
      helpText: 'Encontre em: ZapSign > Configurações > Integrações > API',
    },
  ],
};

// =============================================================================
// COMPONENTE
// =============================================================================

export function SignatureProviderConfigDialog({ open, onOpenChange, provider, existingConfig }: Props) {
  const { upsertConfig, deleteConfig, testConnection, maskCredential } = useTenantSignatureConfigs();
  const { currentTenant } = useAuth();

  // Estado do formulário
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [isActive, setIsActive] = useState(true);
  const [isDefault, setIsDefault] = useState(false);

  // Controle de visibilidade dos campos sensíveis
  const [visibleFields, setVisibleFields] = useState<Record<string, boolean>>({});

  // Estado de loading e resultado do teste
  const [isTesting, setIsTesting] = useState(false);
  // URL do webhook gerada deterministicamente (mesmo padrão do workspace)
  const webhookUrl = provider?.slug === 'autentique'
    ? getEdgeFunctionUrl('signature-webhook') + '?provider=autentique'
    : null;
  const [copiedWebhook, setCopiedWebhook] = useState(false);

  // Campos do provider selecionado
  const fields = provider ? PROVIDER_FIELDS[provider.slug] ?? [] : [];

  // =========================================================================
  // Efeito: Preenche formulário com dados existentes
  // =========================================================================
  useEffect(() => {
    if (existingConfig) {
      // Para edição, mostra campos mascarados
      const maskedCreds: Record<string, string> = {};
      const existCreds = existingConfig.credentials || {};
      for (const field of fields) {
        maskedCreds[field.key] = field.sensitive
          ? maskCredential(existCreds[field.key] || '')
          : existCreds[field.key] || '';
      }
      setCredentials(maskedCreds);
      setIsActive(existingConfig.is_active);
      setIsDefault(existingConfig.is_default);
    } else {
      // Para criação, limpa tudo
      setCredentials({});
      setIsActive(true);
      setIsDefault(false);
    }
    setVisibleFields({});
  }, [existingConfig, provider, open]);

  // =========================================================================
  // Handlers
  // =========================================================================

  /** Atualiza valor de um campo de credencial */
  const handleFieldChange = (key: string, value: string) => {
    setCredentials(prev => ({ ...prev, [key]: value }));
  };

  /** Alterna visibilidade de um campo sensível */
  const toggleFieldVisibility = (key: string) => {
    setVisibleFields(prev => ({ ...prev, [key]: !prev[key] }));
  };

  /** Salva a configuração */
  const handleSave = () => {
    if (!provider) return;

    // Validação: campos obrigatórios
    for (const field of fields) {
      if (field.required) {
        const value = credentials[field.key]?.trim();
        // Se está editando e o valor é mascarado, ignora validação
        if (existingConfig && field.sensitive && value?.startsWith('••••')) continue;
        if (!value) {
          toast.error(`O campo "${field.label}" é obrigatório`);
          return;
        }
      }
    }

    // Monta credenciais finais (mantém valor antigo se campo sensível não foi alterado)
    // Separa webhook_secret das credenciais normais (vai para coluna dedicada)
    const finalCredentials: Record<string, string> = {};
    let webhookSecretValue: string | null = null;

    for (const field of fields) {
      const value = credentials[field.key]?.trim() || '';
      
      // webhook_secret é tratado separadamente
      if (field.key === 'webhook_secret') {
        if (existingConfig && field.sensitive && value.startsWith('••••')) {
          // Não foi alterado, não envia
          webhookSecretValue = null;
        } else if (value) {
          webhookSecretValue = value;
        }
        continue;
      }

      if (existingConfig && field.sensitive && value.startsWith('••••')) {
        finalCredentials[field.key] = existingConfig.credentials[field.key] || '';
      } else {
        finalCredentials[field.key] = value;
      }
    }

    upsertConfig.mutate(
      {
        provider_id: provider.id,
        credentials: finalCredentials,
        is_active: isActive,
        is_default: isDefault,
      },
      {
        onSuccess: async () => {
          // Persiste webhook_secret na coluna dedicada via signature-proxy
          if (webhookSecretValue && currentTenant?.id) {
            try {
              await supabase.functions.invoke('signature-proxy', {
                body: {
                  operation: 'configure',
                  tenant_id: currentTenant.id,
                  provider_id: provider.id,
                  webhook_secret: webhookSecretValue,
                },
              });
              logger.info('Webhook secret salvo na coluna dedicada');
            } catch (err) {
              logger.error('Erro ao salvar webhook_secret', err);
            }
          }
          onOpenChange(false);
        },
      }
    );
  };

  /** Testa a conexão com o provider */
  const handleTestConnection = async () => {
    if (!provider) return;
    setIsTesting(true);

    try {
      // Monta credenciais para teste
      const testCredentials: Record<string, string> = {};
      for (const field of fields) {
        const value = credentials[field.key]?.trim() || '';
        if (existingConfig && field.sensitive && value.startsWith('••••')) {
          testCredentials[field.key] = existingConfig.credentials[field.key] || '';
        } else {
          testCredentials[field.key] = value;
        }
      }

      const result = await testConnection(provider.id, testCredentials);
      if (result?.success) {
        toast.success('Conexão estabelecida com sucesso!');
      } else {
        toast.error(result?.error || 'Falha ao testar conexão');
      }
    } catch (error: unknown) {
      logger.error('Erro ao testar conexão', error);
      const msg = error instanceof Error ? error.message : 'Erro desconhecido';
      toast.error(`Erro ao testar conexão: ${msg}`);
    } finally {
      setIsTesting(false);
    }
  };

  /** Remove a configuração */
  const handleDelete = () => {
    if (!existingConfig) return;
    if (!confirm('Tem certeza que deseja remover esta configuração?')) return;

    deleteConfig.mutate(existingConfig.id, {
      onSuccess: () => onOpenChange(false),
    });
  };

  // =========================================================================
  // Render
  // =========================================================================

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {existingConfig ? 'Editar' : 'Configurar'} {provider?.display_name}
          </DialogTitle>
          <DialogDescription>
            {existingConfig
              ? 'Atualize as credenciais e configurações do provider.'
              : 'Insira as credenciais para integrar com o provider de assinatura digital.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Campos de credenciais */}
          {fields.map((field) => (
            <div key={field.key} className="space-y-2">
              <Label htmlFor={field.key}>
                {field.label}
                {field.required && <span className="text-destructive ml-1">*</span>}
              </Label>

              <div className="relative">
                <Input
                  id={field.key}
                  type={field.sensitive && !visibleFields[field.key] ? 'password' : 'text'}
                  placeholder={field.placeholder}
                  value={credentials[field.key] || ''}
                  onChange={(e) => handleFieldChange(field.key, e.target.value)}
                  className="pr-10"
                />

                {/* Botão para revelar/ocultar campo sensível */}
                {field.sensitive && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => toggleFieldVisibility(field.key)}
                  >
                    {visibleFields[field.key] ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                )}
              </div>

              {/* Texto de ajuda */}
              {field.helpText && (
                <p className="text-xs text-muted-foreground">{field.helpText}</p>
              )}
            </div>
          ))}

          {/* Toggles de status */}
          <div className="flex items-center justify-between pt-2 border-t border-border/50">
            <div>
              <Label className="text-sm font-medium">Ativo</Label>
              <p className="text-xs text-muted-foreground">Habilitar este provider</p>
            </div>
            <Switch checked={isActive} onCheckedChange={setIsActive} />
          </div>

          {/* URL do Webhook (exibida após teste de conexão bem-sucedido) */}
          {webhookUrl && (
            <div className="pt-3 border-t border-border/50 space-y-2">
              <div className="flex items-center gap-2">
                <Link className="h-4 w-4 text-primary" />
                <Label className="text-sm font-medium">URL do Webhook</Label>
              </div>
              <p className="text-xs text-muted-foreground">
                Configure esta URL no Painel do Desenvolvedor da Autentique para receber notificações automáticas de assinatura.
              </p>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={webhookUrl}
                  className="text-xs font-mono bg-muted/50"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  onClick={() => {
                    navigator.clipboard.writeText(webhookUrl);
                    setCopiedWebhook(true);
                    toast.success('URL do webhook copiada!');
                    setTimeout(() => setCopiedWebhook(false), 2000);
                  }}
                >
                  {copiedWebhook ? (
                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              {/* Evento obrigatório */}
              <div className="text-xs text-muted-foreground space-y-1">
                <div>
                  <strong>Tipo "Documento":</strong> selecione apenas
                  <code className="bg-muted px-1.5 py-0.5 rounded text-[10px] ml-1">document.updated</code>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Provider Padrão</Label>
              <p className="text-xs text-muted-foreground">Usar como provider principal</p>
            </div>
            <Switch checked={isDefault} onCheckedChange={setIsDefault} />
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          {/* Botão de deletar (apenas se já configurado) */}
          {existingConfig && (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDelete}
              disabled={deleteConfig.isPending}
              className="mr-auto"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Remover
            </Button>
          )}

          {/* Botão de testar conexão */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleTestConnection}
            disabled={isTesting}
          >
            {isTesting ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Wifi className="h-4 w-4 mr-1" />
            )}
            Testar Conexão
          </Button>

          {/* Botão de salvar */}
          <Button
            onClick={handleSave}
            disabled={upsertConfig.isPending}
          >
            {upsertConfig.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
